import { useMemo } from 'react';
import { CallIntelStore } from '@frontend/api-call-intel';
import { useHasCallIntelAclAccess, useIsCallIntelEligibleAccount } from '@frontend/call-intel-core';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { PATIENT_INSIGHT_PERMISSION, PATIENT_INSIGHT_TEASER_FF } from '../constants';

interface UseCheckPatientInsightEligibilityProps {
  locationId?: string;
}

interface UseCheckPatientInsightEligibilityResult {
  showPatientInsight: boolean;
  showPatientInsightTeaser: boolean;
  isLoading: boolean;
}

export const useCheckPatientInsightEligibility = ({
  locationId,
}: UseCheckPatientInsightEligibilityProps): UseCheckPatientInsightEligibilityResult => {
  const { isDemoAccount } = CallIntelStore.useCallIntelDemoShallowStore('isDemoAccount');
  const { getFeatureFlagValue } = useAppFlagStore();
  const { selectedLocationIds: scopedLocationIds } = useAppScopeStore();

  const selectedLocationIds = useMemo(
    () => (locationId ? [locationId] : scopedLocationIds),
    [locationId, scopedLocationIds]
  );

  const { isCallIntelFreeTrialEligible, isCallIntelActive, isLoading } = useIsCallIntelEligibleAccount({
    locationIds: selectedLocationIds,
    includeAiReceptionist: false,
  });
  const hasPermission = useHasCallIntelAclAccess([PATIENT_INSIGHT_PERMISSION], {
    locations: selectedLocationIds,
    allowDemoOverride: true,
  });
  const isTeaserEnabled = getFeatureFlagValue({
    flagName: PATIENT_INSIGHT_TEASER_FF,
    locationIds: selectedLocationIds,
  });

  const isActiveAndHasAccess = isCallIntelActive && hasPermission;
  const isEligibleForTrialTeaser = !isDemoAccount && isTeaserEnabled && isCallIntelFreeTrialEligible;
  const showPatientInsight = isDemoAccount || isActiveAndHasAccess || isEligibleForTrialTeaser;

  return {
    isLoading,
    showPatientInsight,
    showPatientInsightTeaser: isEligibleForTrialTeaser,
  };
};
